import * as React from 'react'
import { Link } from 'react-router-dom'
import { useIntl } from 'react-intl'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableFooter from '@mui/material/TableFooter'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import IconButton from '@mui/material/IconButton'
import { CircularProgress, TableHead } from '@mui/material'
import ArrowRightIcon from '../../../../assets/icons/Arrows-chevron/Arrow-Right.svg'
import ConfirmDelete from '../../../../components/dialogs/ConfirmDelete'
import { capitalize } from '../../../../utils/functions'
import TablePaginationActions from '../../../../components/muiComponents/tablePaginationActions/TablePaginationActions'
import { GroupState, TeacherState } from '../../../../features/types'
import EditGroupDialog from './EditGroupDialog'

interface Props {
    groups: GroupState[]
    curators: TeacherState[]
    isFetchingCurators: boolean
    onDelete?: (subject: number) => void
}

const GroupsTable: React.FC<Props> = ({
    groups,
    curators,
    isFetchingCurators,
    onDelete,
}) => {
    const { formatMessage, locale } = useIntl()
    const [page, setPage] = React.useState(0)
    const [rowsPerPage, setRowsPerPage] = React.useState(5)

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - groups?.length) : 0

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const getCuratorsOfGroup = (group) => {
        const result = group?.curatorIds?.map?.((curatorId) => {
            const curatorInfo = curators?.find(
                (curator) => curator?.id === curatorId,
            )
            if (curatorInfo) {
                return curatorInfo?.firstName + ' ' + curatorInfo?.lastName
            }
        })

        return curators?.length ? result?.join(', ') : ''
    }

    return (
        <Box>
            <TableContainer
                component={Paper}
                sx={{ border: 1, borderColor: 'grey.100', borderRadius: 2 }}
                elevation={0}
            >
                <Table
                    sx={{ minWidth: 500 }}
                    aria-label="custom pagination table"
                >
                    <TableHead sx={{ bgcolor: 'grey.100' }}>
                        <TableRow>
                            <TableCell>
                                {formatMessage({ id: 'group' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'curators' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'subjects' })}
                            </TableCell>

                            <TableCell align="center">
                                {formatMessage({ id: 'studentCount' })}
                            </TableCell>

                            <TableCell align="right">
                                {formatMessage({ id: 'details' })}
                            </TableCell>

                            <TableCell align="right">
                                {formatMessage({ id: 'edit' })}
                            </TableCell>

                            <TableCell align="right">
                                {formatMessage({ id: 'delete' })}
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {(rowsPerPage > 0
                            ? groups.slice(
                                  page * rowsPerPage,
                                  page * rowsPerPage + rowsPerPage,
                              )
                            : groups
                        ).map((row) => (
                            <TableRow key={row?.id}>
                                <TableCell
                                    component="th"
                                    scope="row"
                                >
                                    {row?.title}
                                </TableCell>

                                <TableCell
                                    component="th"
                                    scope="row"
                                >
                                    {isFetchingCurators ? (
                                        <CircularProgress size={20} />
                                    ) : (
                                        getCuratorsOfGroup(row)
                                    )}
                                </TableCell>

                                <TableCell
                                    component="th"
                                    scope="row"
                                >
                                    {row?.[`subjects${capitalize(locale)}`]}
                                </TableCell>

                                <TableCell
                                    component="th"
                                    scope="row"
                                    align="center"
                                >
                                    {row?.studentCount}
                                </TableCell>

                                <TableCell
                                    component="th"
                                    scope="row"
                                    style={{ width: 60 }}
                                    align="right"
                                >
                                    <Link
                                        to={
                                            '/groups/' +
                                            row?.id +
                                            '?course=' +
                                            row?.courseId +
                                            '&groupName=' +
                                            row?.title
                                        }
                                    >
                                        <IconButton
                                            sx={{ background: '#f3f4f5' }}
                                        >
                                            <img
                                                src={ArrowRightIcon}
                                                alt="edit"
                                            />
                                        </IconButton>
                                    </Link>
                                </TableCell>

                                <TableCell
                                    component="th"
                                    scope="row"
                                    align="right"
                                    style={{ width: 60 }}
                                >
                                    <EditGroupDialog editGroup={row} />
                                </TableCell>

                                <TableCell
                                    component="th"
                                    scope="row"
                                    style={{ width: 60 }}
                                    align="right"
                                >
                                    <ConfirmDelete
                                        confirmAction={() =>
                                            onDelete(row?.id as number)
                                        }
                                    />
                                </TableCell>
                            </TableRow>
                        ))}

                        {emptyRows > 0 && (
                            <TableRow style={{ height: 53 * emptyRows }}>
                                <TableCell colSpan={6} />
                            </TableRow>
                        )}

                        {!groups?.length && (
                            <TableRow style={{ height: 53 * emptyRows }}>
                                <TableCell
                                    component="th"
                                    scope="row"
                                    align="center"
                                    colSpan={7}
                                >
                                    {formatMessage({ id: 'noData' })}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>

                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                rowsPerPageOptions={[
                                    5,
                                    10,
                                    25,
                                    { label: 'All', value: -1 },
                                ]}
                                colSpan={7}
                                count={groups?.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                SelectProps={{
                                    inputProps: {
                                        'aria-label': 'rows per page',
                                    },
                                    native: true,
                                }}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                ActionsComponent={TablePaginationActions}
                                sx={{ border: 0 }}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Box>
    )
}

export default GroupsTable
